import ContentShell from "components/ContentShell";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  Typography,
  Divider,
  Paper,
  Chip,
  Alert,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TuneIcon from "@mui/icons-material/Tune";
import LayersIcon from "@mui/icons-material/Layers";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/banner/bannerEditor";
import { SimpleTextInput } from "components/FormTextInput";
import {
  createChallenge,
  getChallengeInfo,
  updateChallenge,
} from "services/challengeService";
import FilePicker from "components/FilePicker";
import * as Yup from "yup";
import {
  uploadImageV2,
  uploadContentTranslations,
} from "services/uploadService";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTimeDateInput } from "components/FormTimeDateInput";
import TranslationButtons from "components/TranslationButtons";
import useOpenClose from "hooks/useOpenClose";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import SimpleSwitch from "components/Switch";
import sessionHelperInstance from "services/sessionHelper";
import { getPlayerPacks, packInfo } from "services/packServices";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";
import debounce from "lodash.debounce";
import { getUniquesArrayObject } from "utils/miscUtils";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import SearchableInput from "components/SearchableInput";
import { getConfigurations, getStandardOddsKeys } from "services/miscService";
import { getObjectiveTypeTitles } from "services/objectiveTypeTitleService";
import BulkTranslationUpload from "components/BulkTranslationUpload";

// ─── Enum Options ────────────────────────────────────────────
const challengeTierOptions = [
  { id: "STANDARD", label: "STANDARD" },
  { id: "PREMIUM", label: "PREMIUM" },
  { id: "OTHER", label: "OTHER" },
];

const rewardTypeOptions = [
  { id: "PBF_COINS_SMALL", label: "PBF COINS (SMALL)" },
  { id: "PBF_COINS_MEDIUM", label: "PBF COINS (MEDIUM)" },
  { id: "PBF_COINS_LARGE", label: "PBF COINS (LARGE)" },
  { id: "XP", label: "XP" },
  { id: "PACK", label: "PACK" },
  { id: "MYSTERY_PLAYER_CARD", label: "MYSTERY PLAYER CARD" },
];

const scheduleTypeOptions = [
  { id: "ONCE", label: "ONCE" },
  { id: "DAILY", label: "DAILY" },
  { id: "WEEKLY", label: "WEEKLY" },
  { id: "MONTHLY", label: "MONTHLY" },
  { id: "CUSTOM", label: "CUSTOM" },
];

const positionGroupOptions = [
  { id: "Defenders", label: "DEFENDERS (LWB, LB, CB, RB, RWB)" },
  { id: "Midfielders", label: "MIDFIELDERS (CDM, CM, RM, LM)" },
  { id: "Attackers", label: "ATTACKERS (CAM, RW, LW, ST)" },
];

// Maps to ChallengeBetMarketType in game backend (13 values: 8 primary + 5 sub-markets)
const bettingMarketOptions = [
  { id: "FINAL_RESULT", label: "FINAL RESULT" },
  { id: "UNDER_OVER", label: "GOALS OVER / UNDER (ANY)" },
  { id: "UNDER", label: "GOALS UNDER" },
  { id: "OVER", label: "GOALS OVER" },
  { id: "BTTS", label: "BTTS (BOTH TEAMS TO SCORE)" },
  { id: "RESULT_BTTS", label: "RESULT / BTTS" },
  { id: "CORRECT_SCORE", label: "CORRECT SCORE" },
  {
    id: "FIRST_LAST_ANY_GOAL_SCORE",
    label: "FIRST / LAST / ANYTIME GOALSCORER (ANY)",
  },
  { id: "FIRST_GOAL_SCORE", label: "FIRST GOALSCORER" },
  { id: "LAST_GOAL_SCORE", label: "LAST GOALSCORER" },
  { id: "ANY_GOAL_SCORE", label: "ANYTIME GOALSCORER" },
  { id: "HANDICAPS", label: "HANDICAPS" },
];

// ─── Objective Type Hints ────────────────────────────────────
const objectiveTypeDescriptions: Record<
  string,
  { example: string; hint: string }
> = {
  GOALS_SCORED: {
    example: "Score N goals in any match",
    hint: "Counts every goal scored by any player in your team.",
  },
  GOALS_BY_POSITION: {
    example: "Score N goals by a Defender",
    hint: "Only goals scored by players in the selected position group count.",
  },
  GOALS_BY_NATIONALITY: {
    example: "Score N goals with English players",
    hint: "Only goals scored by players of the selected nationality count.",
  },
  GOALS_BY_CLUB: {
    example: "Score N goals with Arsenal players",
    hint: "Only goals scored by players from the selected club count.",
  },
  CONSECUTIVE_WINS: {
    example: "Win N matches in a row",
    hint: "The win streak resets if the player loses or draws.",
  },
  CLEAN_SHEETS: {
    example: "Keep N clean sheets",
    hint: "Counts matches where your team concedes 0 goals.",
  },
  CONSECUTIVE_CLEAN_SHEETS: {
    example: "Keep N clean sheets in a row",
    hint: "The streak resets if any goal is conceded.",
  },
  CONSECUTIVE_SCORING: {
    example: "Score in N consecutive matches",
    hint: "Player must score at least one goal in each consecutive match.",
  },
  WINS_WITH_NATIONALITY: {
    example: "Win N matches with ≥ 3 Brazilian players",
    hint: "Requires a minimum number of players from the chosen nationality in the lineup.",
  },
  WINS_WITH_CLUB: {
    example: "Win N matches with ≥ 3 Arsenal players",
    hint: "Requires a minimum number of players from the chosen club in the lineup.",
  },
  BETS_PLACED: {
    example: "Place N bets",
    hint: "Counts all settled bets regardless of betting market.",
  },
  BETS_PLACED_MARKET: {
    example: "Place N bets on Correct Score markets",
    hint: "Counts settled bets matching the selected betting market.",
  },
  BET_WIN_BY_ODDS: {
    example: "Win N bets with odds > 2.5",
    hint: "Only resolved winning bets whose decimal odds meet the minimum threshold count.",
  },
  CONSECUTIVE_BETS: {
    example: "Place bets in N consecutive matches",
    hint: "The streak resets if the player does not place any bet in a match.",
  },
  CONSECUTIVE_BETS_ON_MARKET: {
    example: "Place bets on Correct Score in N consecutive matches",
    hint: "The streak resets if the player does not place a bet on the selected market in a match.",
  },
  LEAGUE_POINTS: {
    example: "Accumulate N league points",
    hint: "Tracks total points earned in league matches.",
  },
  VAULT_LEVEL_UP: {
    example: "Level up your vault N times",
    hint: "Each vault level-up increments the counter by 1.",
  },
  GET_PROMOTED: {
    example: "Get promoted to a higher division {{text1}} time(s)",
    hint: "Awarded each time the player clears the last milestone of their current division and advances to the next higher division (e.g. Division 5 → Division 4). Use {{text1}} in the title template for the target count. Set Objective Target to 1 for a single promotion, or higher for a multi-promotion challenge.",
  },
  PLAY_DAYS_THIS_WEEK: {
    example: "Play PBF for 5 days this week",
    hint: "Counts the number of distinct calendar days (UTC) the player completes at least one match during the current weekly cycle. Multiple matches on the same day count as 1 day.",
  },
};

// ─── Types ────────────────────────────────────────────────────
interface ObjectiveFilters {
  position?: string;
  nationality?: string;
  clubId?: string;
  minOdds?: number | null;
  minPlayers?: number | null;
  betType?: string;
}

interface SubChallenge {
  _id?: string;
  tier: string;
  objectiveType: string;
  objectiveTarget: number | null;
  objectiveFilters: ObjectiveFilters;
  rewardType: string;
  rewardValue: number | null;
  rewardPacks: string[];
  rewardImageUrl?: string;
}

interface ChallengeDetails {
  type: string;
  tier: string;
  objectiveType: string;
  objectiveTarget: number | null;
  objectiveFilters: ObjectiveFilters;
  rewardType: string;
  rewardValue: number | null;
  rewardPacks: string[];
  rewardImageUrl?: string;
  comboTitle: string;
  comboTitleTrans: Record<string, any>;
  comboRewardTitle: string;
  comboRewardTitleTrans: Record<string, any>;
  startDate: string;
  endDate: string;
  scheduleType: string;
  repeatEveryDays: number | null;
  operators: any[];
  excludedOperators: any[];
  sortOrder: number | null;
  isActive: boolean;
  subChallenges: SubChallenge[];
}

const _emptySubChallenge: SubChallenge = {
  tier: "",
  objectiveType: "",
  objectiveTarget: null,
  objectiveFilters: {},
  rewardType: "",
  rewardValue: null,
  rewardPacks: [],
};

const _challengeDetails: ChallengeDetails = {
  type: "INDIVIDUAL",
  tier: "STANDARD",
  objectiveType: "",
  objectiveTarget: null,
  objectiveFilters: {},
  rewardType: "",
  rewardValue: null,
  rewardPacks: [],
  comboTitle: "",
  comboTitleTrans: {},
  comboRewardTitle: "",
  comboRewardTitleTrans: {},
  startDate: "",
  endDate: "",
  scheduleType: "ONCE",
  repeatEveryDays: null,
  operators: [],
  excludedOperators: [],
  sortOrder: null,
  isActive: true,
  subChallenges: [],
};

// ─── Validation ─────────────────────────────────────────────
const challengeValidation = Yup.object().shape({
  type: Yup.string()
    .oneOf(["INDIVIDUAL", "COMBO"])
    .required("Type is required"),
  tier: Yup.string()
    .nullable()
    .when("type", {
      is: "INDIVIDUAL",
      then: (schema) =>
        schema.required("Tier is required for Individual challenges"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  startDate: Yup.string().required("Start date is required"),
  endDate: Yup.string().required("End date is required"),
  scheduleType: Yup.string().required("Schedule type is required"),
  repeatEveryDays: Yup.number()
    .nullable()
    .when("scheduleType", {
      is: "CUSTOM",
      then: (schema) =>
        schema
          .required("Cycle interval is required for Custom schedule")
          .min(1, "Cycle interval must be at least 1 day"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  rewardType: Yup.string().required("Reward type is required"),
  rewardValue: Yup.number()
    .required("Reward value is required")
    .positive("Reward value must be positive"),
  objectiveType: Yup.string()
    .nullable()
    .when("type", {
      is: "INDIVIDUAL",
      then: (schema) => schema.required("Objective type is required"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  objectiveTarget: Yup.number()
    .nullable()
    .when("type", {
      is: "INDIVIDUAL",
      then: (schema) =>
        schema
          .required("Objective target is required")
          .positive("Target must be positive"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

// ─── Helpers ────────────────────────────────────────────────
const needsPositionFilter = (objectiveType: string) =>
  objectiveType === "GOALS_BY_POSITION";

const needsNationalityFilter = (objectiveType: string) =>
  ["GOALS_BY_NATIONALITY", "WINS_WITH_NATIONALITY"].includes(objectiveType);

const needsClubFilter = (objectiveType: string) =>
  ["GOALS_BY_CLUB", "WINS_WITH_CLUB"].includes(objectiveType);

const needsMinOdds = (objectiveType: string) =>
  objectiveType === "BET_WIN_BY_ODDS";

const getNearbyOddsExamples = (value: number, keysSet: Set<string>): string => {
  const sorted = Array.from(keysSet)
    .map(Number)
    .sort((a, b) => a - b);
  const insertIdx = sorted.findIndex((k) => k > value);
  const idx = insertIdx === -1 ? sorted.length : insertIdx;
  const before = sorted.slice(Math.max(0, idx - 2), idx);
  const after = sorted.slice(idx, Math.min(sorted.length, idx + 3));
  return [...before, ...after].join(", ");
};

const needsMinPlayers = (objectiveType: string) =>
  ["WINS_WITH_NATIONALITY", "WINS_WITH_CLUB"].includes(objectiveType);

const needsBetTypeFilter = (objectiveType: string) =>
  ["BETS_PLACED_MARKET", "CONSECUTIVE_BETS_ON_MARKET"].includes(objectiveType);

const replaceTemplatePlaceholders = (template: string, values: string[]) => {
  if (!template) return "";
  return values.reduce((acc, value, idx) => {
    const token = new RegExp(`\\{\\{text${idx + 1}\\}\\}`, "g");
    return acc.replace(token, value);
  }, template);
};

const extractFilterValue = (value: any): any => {
  if (Array.isArray(value)) return extractFilterValue(value[0]);
  if (value && typeof value === "object") {
    return (
      value.id ||
      value._id ||
      value.value ||
      value.code ||
      value.iso2 ||
      value.label ||
      ""
    );
  }
  return value;
};

const normalizeObjectiveFilters = (filters: any): ObjectiveFilters => {
  const safeFilters = filters || {};
  const minOddsRaw = extractFilterValue(safeFilters.minOdds);
  const minPlayersRaw = extractFilterValue(safeFilters.minPlayers);

  return {
    position: extractFilterValue(safeFilters.position) || undefined,
    nationality: extractFilterValue(safeFilters.nationality) || undefined,
    clubId: extractFilterValue(safeFilters.clubId) || undefined,
    betType: extractFilterValue(safeFilters.betType) || undefined,
    minOdds:
      minOddsRaw === null || minOddsRaw === undefined || minOddsRaw === ""
        ? null
        : Number(minOddsRaw),
    minPlayers:
      minPlayersRaw === null ||
      minPlayersRaw === undefined ||
      minPlayersRaw === ""
        ? null
        : Number(minPlayersRaw),
  };
};

// ─── Objective Filter Validation ────────────────────────────
/**
 * Throws an Error if any required filter field for the given objectiveType is missing.
 * @param objectiveType  The objective type string
 * @param filters        The current ObjectiveFilters for that objective
 * @param prefix         Label prefix used in the error message, e.g. "" or "Sub-challenge #2: "
 */
const validateObjectiveFilters = (
  objectiveType: string,
  filters: ObjectiveFilters | null | undefined,
  standardOddsKeys: Set<string>,
  prefix = "",
) => {
  const f = filters || {};

  if (needsPositionFilter(objectiveType) && !f.position) {
    throw new Error(
      `${prefix}Position Group is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (needsNationalityFilter(objectiveType) && !f.nationality) {
    throw new Error(
      `${prefix}Nationality is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (needsClubFilter(objectiveType) && !f.clubId) {
    throw new Error(
      `${prefix}Club is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (
    needsMinPlayers(objectiveType) &&
    (f.minPlayers === null ||
      f.minPlayers === undefined ||
      f.minPlayers === ("" as any))
  ) {
    throw new Error(
      `${prefix}Min Players is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (
    needsMinOdds(objectiveType) &&
    (f.minOdds === null || f.minOdds === undefined || f.minOdds === ("" as any))
  ) {
    throw new Error(
      `${prefix}Minimum Odds is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (
    needsMinOdds(objectiveType) &&
    f.minOdds != null &&
    f.minOdds !== ("" as any) &&
    standardOddsKeys.size > 0 &&
    !standardOddsKeys.has(String(f.minOdds))
  ) {
    throw new Error(
      `${prefix}Minimum Odds value ${f.minOdds} is not a valid standard odds value. Please enter a value that exists in the standard odds table.`,
    );
  }

  if (needsBetTypeFilter(objectiveType) && !f.betType) {
    throw new Error(
      `${prefix}Betting Market Type is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }
};

// ─── Visual Data Constants ──────────────────────────────────
const rewardTypeInfo: Record<
  string,
  { icon: string; color: string; desc: string }
> = {
  PBF_COINS_SMALL: {
    icon: "🪙",
    color: "#ffa726",
    desc: "A small coin reward for quick wins",
  },
  PBF_COINS_MEDIUM: {
    icon: "💰",
    color: "#ff9800",
    desc: "A medium coin reward for mid-tier challenges",
  },
  PBF_COINS_LARGE: {
    icon: "💎",
    color: "#ef6c00",
    desc: "A large coin reward for major achievements",
  },
  XP: {
    icon: "⚡",
    color: "#7c4dff",
    desc: "Experience points to level up faster",
  },
  PACK: {
    icon: "📦",
    color: "#2e7d32",
    desc: "One or more player card packs",
  },
  MYSTERY_PLAYER_CARD: {
    icon: "🎴",
    color: "#6a1b9a",
    desc: "A random mystery player card",
  },
};

const tierVisuals: Record<
  string,
  { bg: string; border: string; text: string; icon: string }
> = {
  STANDARD: {
    bg: "#e3f2fd",
    border: "#1976d2",
    text: "#1565c0",
    icon: "⭐",
  },
  PREMIUM: {
    bg: "#fff8e1",
    border: "#f9a825",
    text: "#e65100",
    icon: "👑",
  },
  OTHER: {
    bg: "#f3e5f5",
    border: "#9c27b0",
    text: "#7b1fa2",
    icon: "🔮",
  },
};

const scheduleInfo: Record<string, { icon: string; desc: string }> = {
  ONCE: {
    icon: "🎯",
    desc: "Challenge runs once between start and end date",
  },
  DAILY: {
    icon: "📅",
    desc: "Resets every day — each daily cycle is tracked separately",
  },
  WEEKLY: {
    icon: "📆",
    desc: "Resets every week — each weekly cycle is tracked separately",
  },
  MONTHLY: {
    icon: "🗓️",
    desc: "Resets every month — each monthly cycle is tracked separately",
  },
  CUSTOM: {
    icon: "⚙️",
    desc: "Resets on a custom interval — you define the cycle length in days",
  },
};

const objectiveCategoryColor: Record<string, string> = {
  GOALS_SCORED: "#2e7d32",
  GOALS_BY_POSITION: "#2e7d32",
  GOALS_BY_NATIONALITY: "#2e7d32",
  GOALS_BY_CLUB: "#2e7d32",
  CONSECUTIVE_WINS: "#1565c0",
  CLEAN_SHEETS: "#00838f",
  CONSECUTIVE_CLEAN_SHEETS: "#00838f",
  CONSECUTIVE_SCORING: "#6a1b9a",
  WINS_WITH_NATIONALITY: "#1565c0",
  WINS_WITH_CLUB: "#1565c0",
  BETS_PLACED: "#e65100",
  BETS_PLACED_MARKET: "#e65100",
  BET_WIN_BY_ODDS: "#e65100",
  LEAGUE_POINTS: "#37474f",
  VAULT_LEVEL_UP: "#f57c00",
  GET_PROMOTED: "#1565c0",
  PLAY_DAYS_THIS_WEEK: "#00695c",
};

const getProgressSteps = (d: ChallengeDetails) => [
  { label: "Type", done: !!d.type },
  ...(d.type === "COMBO"
    ? [{ label: "Combo Title", done: !!d.comboTitle }]
    : []),
  {
    label: "Objective",
    done:
      d.type === "COMBO"
        ? d.subChallenges.length > 0 &&
          d.subChallenges.every(
            (s) => !!s.objectiveType && (s.objectiveTarget ?? 0) > 0,
          )
        : !!d.objectiveType && (d.objectiveTarget ?? 0) > 0,
  },
  { label: "Reward", done: !!d.rewardType && (d.rewardValue ?? 0) > 0 },
  {
    label: "Schedule",
    done: !!d.startDate && !!d.endDate && !!d.scheduleType,
  },
];

// ─── Section Card ───────────────────────────────────────────
const SectionCard = ({
  step,
  icon,
  title,
  subtitle,
  accent = "#1976d2",
  children,
  isComplete,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: string;
  children: React.ReactNode;
  isComplete?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: isComplete ? `${accent}60` : "divider",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      transition: "border-color 0.3s, box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}12` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}40`,
        }}
      >
        {step}
      </Box>
      <Box sx={{ color: accent, display: "flex", fontSize: 20 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.primary"
          sx={{ lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {isComplete !== undefined && (
        <Tooltip
          title={isComplete ? "Section complete" : "Fill in required fields"}
        >
          <Box
            sx={{
              display: "flex",
              color: isComplete ? "#4caf50" : "#bdbdbd",
            }}
          >
            {isComplete ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" />
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

// ─── Component ───────────────────────────────────────────────
const ChallengesEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const isEdit = id !== "create";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [challengeDetails, setChallengeDetails] = useState<ChallengeDetails>(
    () => {
      if (isEdit) return _challengeDetails;
      // Default: today at midnight → tomorrow at midnight
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return {
        ..._challengeDetails,
        isActive: false,
        startDate: today.toISOString(),
        endDate: tomorrow.toISOString(),
      };
    },
  );
  const [rewardImage, setRewardImage] = useState<any | null>(null);
  const [subRewardImages, setSubRewardImages] = useState<(any | null)[]>([]);
  // Image dimension states — powers the "Image Dimensions" control in FilePicker
  const [rewardImageDimensions, setRewardImageDimensions] = useState<
    any | null
  >(null);
  const [subRewardImageDimensions, setSubRewardImageDimensions] = useState<
    (any | null)[]
  >([]);
  const [packOptions, setPackOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [packSelections, setPackSelections] = useState<
    ({ id: string; label: string } | null)[]
  >([]);
  const [subPackSelections, setSubPackSelections] = useState<
    ({ id: string; label: string } | null)[][]
  >([]);
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const { isOpen, close, open } = useOpenClose();
  const [objectiveTypeOptions, setObjectiveTypeOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [objectiveTypeTitleMap, setObjectiveTypeTitleMap] = useState<
    Record<string, string>
  >({});
  const [standardOddsKeys, setStandardOddsKeys] = useState<Set<string>>(
    new Set(),
  );
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null as string | null,
    viewData: [] as any[],
    modal: "" as string,
    subChallengeIndex: null as number | null,
  });

  const cancel = () => navigate(configData.routes.challenges.challenges);

  // ─── Submit ─────────────────────────────────────────────
  const submit = async () => {
    try {
      setLoading(true);
      await challengeValidation.validate(challengeDetails);

      if (isPackRewardType(challengeDetails.rewardType)) {
        const rewardQty = Number(challengeDetails.rewardValue);
        if (!Number.isInteger(rewardQty) || rewardQty < 1) {
          throw new Error(
            "Reward quantity must be a whole number greater than 0 for PACK and MYSTERY PLAYER CARD rewards",
          );
        }
        if ((challengeDetails.rewardPacks || []).length !== rewardQty) {
          throw new Error(
            `Please select exactly ${rewardQty} pack ID${rewardQty > 1 ? "s" : ""} for the configured quantity`,
          );
        }
      }

      // Validate objective filters for INDIVIDUAL challenges
      if (challengeDetails.type === "INDIVIDUAL") {
        validateObjectiveFilters(
          challengeDetails.objectiveType,
          challengeDetails.objectiveFilters,
          standardOddsKeys,
        );
      }

      if (
        challengeDetails.type === "COMBO" &&
        (!challengeDetails.subChallenges ||
          challengeDetails.subChallenges.length === 0)
      ) {
        throw new Error(
          "Combo challenges must have at least one sub-challenge",
        );
      }

      if (challengeDetails.type === "COMBO" && !challengeDetails.comboTitle) {
        throw new Error("Combo Title is required for COMBO challenges");
      }

      // Validate each sub-challenge field for COMBO type
      if (challengeDetails.type === "COMBO") {
        challengeDetails.subChallenges.forEach((sub, i) => {
          const n = i + 1;
          if (!sub.objectiveType)
            throw new Error(`Sub-challenge #${n}: Objective type is required`);
          if (
            sub.objectiveTarget === null ||
            sub.objectiveTarget === undefined ||
            Number(sub.objectiveTarget) < 1
          )
            throw new Error(
              `Sub-challenge #${n}: Objective target must be at least 1`,
            );
          if (!sub.rewardType)
            throw new Error(`Sub-challenge #${n}: Reward type is required`);
          if (
            sub.rewardValue === null ||
            sub.rewardValue === undefined ||
            Number(sub.rewardValue) < 0
          )
            throw new Error(
              `Sub-challenge #${n}: Reward value must be 0 or greater`,
            );

          if (isPackRewardType(sub.rewardType)) {
            const subQty = Number(sub.rewardValue);
            if (!Number.isInteger(subQty) || subQty < 1) {
              throw new Error(
                `Sub-challenge #${n}: Quantity must be a whole number greater than 0 for PACK and MYSTERY PLAYER CARD rewards`,
              );
            }
            if ((sub.rewardPacks || []).length !== subQty) {
              throw new Error(
                `Sub-challenge #${n}: Please select exactly ${subQty} pack ID${subQty > 1 ? "s" : ""}`,
              );
            }
          }

          // Validate objective filters for this sub-challenge
          validateObjectiveFilters(
            sub.objectiveType,
            sub.objectiveFilters,
            standardOddsKeys,
            `Sub-challenge #${n}: `,
          );
        });
      }

      let response;
      const payload = buildPayload();

      if (!isEdit) {
        response = await createChallenge(payload);
      } else {
        const { isActive, ...rest } = payload;
        response = await updateChallenge(rest, id);
      }

      // Upload images based on presigned URLs from backend response
      const challengeData = response?.data;
      if (challengeData) {
        // 1. Main reward image (shared for all reward types including PACK)
        if (challengeData.uploadRewardImageUrl && rewardImage) {
          await upload(
            challengeData.uploadRewardImageUrl,
            rewardImage,
            rewardImageDimensions,
          );
        }

        // 2. Sub-challenge reward images
        if (challengeData.subChallenges?.length) {
          for (let si = 0; si < challengeData.subChallenges.length; si++) {
            const subData = challengeData.subChallenges[si];

            if (subData.uploadRewardImageUrl && subRewardImages[si]) {
              await upload(
                subData.uploadRewardImageUrl,
                subRewardImages[si],
                subRewardImageDimensions[si],
              );
            }
          }
        }
      }

      // Upload translation file for COMBO challenges (create mode only)
      if (
        !isEdit &&
        translationFile &&
        challengeData?._id &&
        challengeDetails.type === "COMBO"
      ) {
        await uploadContentTranslations(
          translationFile,
          "CHALLENGE",
          challengeData._id,
        );
      }

      showSnackbar(response?.message || "Challenge saved", "success");
      setLoading(false);
      navigate(configData.routes.challenges.challenges);
    } catch (error: any) {
      if (error?.inner?.length)
        error.inner.forEach((err: any) => showSnackbar(err.message, "error"));
      else showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const isPackReward = ["PACK", "MYSTERY_PLAYER_CARD"].includes(
      challengeDetails.rewardType,
    );
    const base: any = {
      type: challengeDetails.type,
      tier: challengeDetails.tier || undefined,
      rewardType: challengeDetails.rewardType,
      rewardValue: Number(challengeDetails.rewardValue),
      startDate: challengeDetails.startDate,
      endDate: challengeDetails.endDate,
      scheduleType: challengeDetails.scheduleType,
      ...(challengeDetails.repeatEveryDays
        ? { repeatEveryDays: Number(challengeDetails.repeatEveryDays) }
        : {}),
      operators: challengeDetails.operators,
      excludedOperators: challengeDetails.excludedOperators,
      sortOrder: challengeDetails.sortOrder
        ? Number(challengeDetails.sortOrder)
        : 0,
      isActive: challengeDetails.isActive,
    };

    // Reward image — shared for all reward types (coins, XP, PACK, etc.)
    if (rewardImage) {
      base.fileType = "image/webp";
    }

    // rewardPacks — flat array of pack IDs
    if (isPackReward && challengeDetails.rewardPacks.length > 0) {
      base.rewardPacks = challengeDetails.rewardPacks.filter(Boolean);
    }

    // Combo fields
    if (challengeDetails.type === "COMBO") {
      if (challengeDetails.comboTitle) {
        base.comboTitle = challengeDetails.comboTitle;
        base.comboTitleTrans = challengeDetails.comboTitleTrans;
      }
      if (challengeDetails.comboRewardTitle) {
        base.comboRewardTitle = challengeDetails.comboRewardTitle;
        base.comboRewardTitleTrans = challengeDetails.comboRewardTitleTrans;
      }
    }

    if (challengeDetails.type === "INDIVIDUAL") {
      base.objectiveType = challengeDetails.objectiveType;
      base.objectiveTarget = Number(challengeDetails.objectiveTarget);
      base.objectiveFilters = buildFilters(
        challengeDetails.objectiveType,
        challengeDetails.objectiveFilters,
      );
    } else {
      base.subChallenges = challengeDetails.subChallenges.map((sub, idx) => {
        const isSubPackReward = ["PACK", "MYSTERY_PLAYER_CARD"].includes(
          sub.rewardType,
        );
        const subEntry: any = {
          // Include _id for existing sub-challenges (edit mode) so backend can preserve images
          ...(isEdit && sub._id ? { _id: sub._id } : {}),
          tier: sub.tier || undefined,
          objectiveType: sub.objectiveType,
          objectiveTarget: Number(sub.objectiveTarget),
          objectiveFilters: buildFilters(
            sub.objectiveType,
            sub.objectiveFilters,
          ),
          rewardType: sub.rewardType,
          rewardValue: Number(sub.rewardValue),
        };
        // Reward image for sub-challenge
        if (subRewardImages[idx]) {
          subEntry.fileType = "image/webp";
        }
        // rewardPacks — flat array of pack IDs
        if (isSubPackReward && sub.rewardPacks?.length > 0) {
          subEntry.rewardPacks = sub.rewardPacks.filter(Boolean);
        }
        return subEntry;
      });
    }

    return base;
  };

  const buildFilters = (
    objectiveType: string,
    filters: ObjectiveFilters | null | undefined,
  ): ObjectiveFilters => {
    const safeFilters = filters || {};
    const result: ObjectiveFilters = {};
    if (needsPositionFilter(objectiveType) && safeFilters.position)
      result.position = safeFilters.position;
    if (needsNationalityFilter(objectiveType) && safeFilters.nationality)
      result.nationality = safeFilters.nationality;
    if (needsClubFilter(objectiveType) && safeFilters.clubId)
      result.clubId = safeFilters.clubId;
    if (needsMinOdds(objectiveType) && safeFilters.minOdds)
      result.minOdds = Number(safeFilters.minOdds);
    if (needsMinPlayers(objectiveType) && safeFilters.minPlayers)
      result.minPlayers = Number(safeFilters.minPlayers);
    if (needsBetTypeFilter(objectiveType) && safeFilters.betType)
      result.betType = safeFilters.betType;
    return result;
  };

  // ─── Handlers ───────────────────────────────────────────
  const onChange = (e: any) => {
    const { name, value } = e?.target;
    let processedValue = value;

    if (["objectiveTarget", "rewardValue", "sortOrder"].includes(name)) {
      processedValue = value === "" ? null : Number(value);
    }

    let data: any = {
      ...challengeDetails,
      [name]: ["isActive"].includes(name)
        ? !challengeDetails[name]
        : processedValue,
    };

    // Reset rewardPacks / reward image when rewardType changes
    if (name === "rewardType") {
      data.rewardPacks = [];
      data.rewardImageUrl = undefined;
      setPackSelections([]);
      setRewardImage(null);
      setRewardImageDimensions(null);
    }

    // When switching type, preserve existing data so users can switch back without losing work.
    // Only seed a default sub-challenge when switching to COMBO with none present.
    if (name === "type") {
      if (value === "COMBO" && data.subChallenges.length === 0) {
        data.subChallenges = [{ ..._emptySubChallenge }];
      }
    }

    // Clear repeatEveryDays when switching away from CUSTOM
    if (name === "scheduleType" && value !== "CUSTOM") {
      data.repeatEveryDays = null;
    }

    setChallengeDetails(data);
  };

  const onFilterChange = (field: string, value: any) => {
    setChallengeDetails((prev) => ({
      ...prev,
      objectiveFilters: { ...prev.objectiveFilters, [field]: value },
    }));
  };

  const onDateChange = (name: string, value: any) => {
    const isoValue = value ? value.toISOString() : "";
    setChallengeDetails((prev) => ({ ...prev, [name]: isoValue }));
  };

  // ─── Sub-challenge handlers ─────────────────────────────
  const addSubChallenge = () => {
    setChallengeDetails((prev) => ({
      ...prev,
      subChallenges: [...prev.subChallenges, { ..._emptySubChallenge }],
    }));
    setSubRewardImages((prev) => [...prev, null]);
    setSubRewardImageDimensions((prev) => [...prev, null]);
    setSubPackSelections((prev) => [...prev, []]);
  };

  const removeSubChallenge = (index: number) => {
    setChallengeDetails((prev) => ({
      ...prev,
      subChallenges: prev.subChallenges.filter((_, i) => i !== index),
    }));
    setSubRewardImages((prev) => prev.filter((_, i) => i !== index));
    setSubRewardImageDimensions((prev) => prev.filter((_, i) => i !== index));
    setSubPackSelections((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubChallengeChange = (index: number, field: string, value: any) => {
    setChallengeDetails((prev) => {
      const subs = [...prev.subChallenges];
      const sub = { ...subs[index] };

      if (["objectiveTarget", "rewardValue"].includes(field)) {
        sub[field] = value === "" ? null : Number(value);
      } else {
        sub[field] = value;
      }

      if (field === "objectiveType") {
        sub.objectiveFilters = {};
      }

      // Reset rewardPacks / reward image when rewardType changes
      if (field === "rewardType") {
        sub.rewardPacks = [];
        sub.rewardImageUrl = undefined;
        setSubPackSelections((prev) => {
          const next = [...prev];
          next[index] = [];
          return next;
        });
        setSubRewardImages((prev) => {
          const next = [...prev];
          next[index] = null;
          return next;
        });
        setSubRewardImageDimensions((prev) => {
          const next = [...prev];
          next[index] = null;
          return next;
        });
      }

      subs[index] = sub;
      return { ...prev, subChallenges: subs };
    });
  };

  const onSubFilterChange = (index: number, field: string, value: any) => {
    setChallengeDetails((prev) => {
      const subs = [...prev.subChallenges];
      subs[index] = {
        ...subs[index],
        objectiveFilters: {
          ...(subs[index].objectiveFilters || {}),
          [field]: value,
        },
      };
      return { ...prev, subChallenges: subs };
    });
  };

  // ─── Image ─────────────────────────────────────────────
  const upload = async (url: string, img: any, dimensions?: any) => {
    const defaultDims =
      DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE];
    try {
      const resp = await uploadImageV2({
        data: img,
        uploadUrl: url,
        resize: true,
        resizeToWidth: dimensions?.targetWidth || defaultDims.width,
        resizeToHeight: dimensions?.targetHeight || defaultDims.height,
      });
      if (resp.status) {
        showSnackbar(resp.message ?? "Image Uploaded", "success");
        return resp;
      } else {
        throw new Error(
          resp.message || `Image upload failed with status ${resp.status}`,
        );
      }
    } catch (error: any) {
      showSnackbar(error.message || "Error uploading image!", "error");
      throw error;
    }
  };

  // ─── Fetch ─────────────────────────────────────────────
  const fetchChallengeInfo = async () => {
    try {
      setLoading(true);
      const resp = await getChallengeInfo(id);
      if (resp?.data) {
        const subChallenges = (resp.data.subChallenges || []).map(
          (sub: any) => {
            return {
              ...sub,
              tier: sub.tier || "",
              objectiveFilters: normalizeObjectiveFilters(sub.objectiveFilters),
              rewardPacks: (sub.rewardPacks || []).map((p: any) =>
                typeof p === "string" ? p : p?.packId || p,
              ),
              rewardImageUrl: sub.rewardImageUrl || undefined,
            };
          },
        );

        setChallengeDetails({
          ..._challengeDetails,
          ...resp.data,
          operators: resp.data.operators || [],
          excludedOperators: resp.data.excludedOperators || [],
          subChallenges,
          objectiveFilters: normalizeObjectiveFilters(
            resp.data.objectiveFilters,
          ),
          rewardPacks: (resp.data.rewardPacks || []).map((p: any) =>
            typeof p === "string" ? p : p?.packId || p,
          ),
          rewardImageUrl: resp.data.rewardImageUrl || undefined,
          comboTitle: resp.data.comboTitle || "",
          comboTitleTrans: resp.data.comboTitleTrans || {},
          comboRewardTitle: resp.data.comboRewardTitle || "",
          comboRewardTitleTrans: resp.data.comboRewardTitleTrans || {},
        });
        // Sync image arrays (no actual files yet — existing images are URLs)
        setRewardImage(null);
        setSubRewardImages(
          new Array((resp.data.subChallenges || []).length).fill(null),
        );

        // Resolve labels for pre-selected packs (edit mode)
        const resolvePackLabel = async (pid: string) => {
          if (!pid) return null;
          try {
            const info = await packInfo(pid);
            if (info?.data) return { id: info.data._id, label: info.data.name };
          } catch {}
          return { id: pid, label: pid };
        };

        const mainPackIds = (resp.data.rewardPacks || []).map((p: any) =>
          typeof p === "string" ? p : p?.packId || p,
        );
        const resolvedMain = await Promise.all(
          mainPackIds.map(resolvePackLabel),
        );
        setPackSelections(resolvedMain);
        setPackOptions(
          (prev) =>
            getUniquesArrayObject([
              ...(resolvedMain.filter(Boolean) as {
                id: string;
                label: string;
              }[]),
              ...prev,
            ]) as { id: string; label: string }[],
        );

        const resolvedSubs = await Promise.all(
          (resp.data.subChallenges || []).map(async (sub: any) => {
            const subPackIds = (sub.rewardPacks || []).map((p: any) =>
              typeof p === "string" ? p : p?.packId || p,
            );
            return await Promise.all(subPackIds.map(resolvePackLabel));
          }),
        );
        setSubPackSelections(resolvedSubs);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  // ─── Translation Modal ─────────────────────────────────
  const getTranslatedData = (field: string, subIdx?: number | null) => {
    const source =
      subIdx != null
        ? challengeDetails.subChallenges[subIdx]?.[field] || {}
        : challengeDetails?.[field] || {};
    const _translatedData = Object.entries(source).map(([key, value]) => ({
      lang: key,
      inputValue: value,
    }));
    return _translatedData || [];
  };

  const handleOpenModal = (
    type: string,
    field: string,
    subIdx?: number | null,
  ) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field, subIdx);
        return {
          ...state,
          viewData: data,
          field,
          modal: type,
          subChallengeIndex: subIdx ?? null,
        };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
          subChallengeIndex: subIdx ?? null,
        };
      }
    });
    open();
  };

  const handleModalChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default language",
        "error",
      );
      return;
    }

    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    const subIdx = modalData.subChallengeIndex;
    setChallengeDetails((prev) => {
      if (subIdx != null) {
        const subs = [...prev.subChallenges];
        const sub = { ...subs[subIdx] };
        const fieldKey = modalData.field!;
        sub[fieldKey] = Object.keys(sub[fieldKey] || {}).reduce(
          (acc: any, key: string) => {
            if (key !== data.lang) acc[key] = (sub[fieldKey] as any)[key];
            return acc;
          },
          {},
        );
        subs[subIdx] = sub;
        return { ...prev, subChallenges: subs };
      }
      const fieldKey = modalData.field!;
      prev[fieldKey] = Object.keys(prev[fieldKey]).reduce(
        (acc: any, key: string) => {
          if (key !== data.lang) {
            acc[key] = prev[fieldKey][key];
          }
          return acc;
        },
        {},
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field, subChallengeIndex: subIdx } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    if (subIdx != null) {
      // Sub-challenge translation
      setChallengeDetails((prev) => {
        const subs = [...prev.subChallenges];
        const sub = { ...subs[subIdx] };
        sub[field] = {
          ...((sub[field] as any) || {}),
          [modalData.language]: modalData.inputValue,
        };
        if (modalData.language === "en" && modalData.inputValue !== "") {
          const baseField = field.split("Trans")[0];
          sub[baseField] = modalData.inputValue;
        }
        subs[subIdx] = sub;
        return { ...prev, subChallenges: subs };
      });
    } else {
      // Main challenge translation
      setChallengeDetails((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          [modalData.language]: modalData.inputValue,
        },
      }));

      if (modalData.language === "en" && modalData.inputValue !== "") {
        const baseField = field.split("Trans")[0];
        setChallengeDetails((details) => ({
          ...details,
          [baseField]: modalData.inputValue,
          [field]: { ...details[field], en: modalData.inputValue },
        }));
      }
    }

    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
      subChallengeIndex: null,
    }));
    close();
  };

  const fetchStandardOddsKeys = async () => {
    try {
      const keys = await getStandardOddsKeys();
      if (keys?.length) setStandardOddsKeys(new Set(keys));
    } catch {
      // Silently fail — validation will be skipped if keys cannot be loaded
    }
  };

  const fetchObjectiveTypeConfigs = async () => {
    try {
      const resp = await getConfigurations();
      if (resp?.data?.length) {
        const otConfig = resp.data.find(
          (c: any) => c.type === "OBJECTIVE_TYPE_CONFIG",
        );
        const configs = otConfig?.data?.objectiveTypes || [];
        setObjectiveTypeOptions(
          configs.map((c: any) => ({
            id: c.name,
            label: c.name.replace(/_/g, " "),
          })),
        );
      }
    } catch {
      // Silently fail — options will be empty
    }
  };

  const fetchObjectiveTypeTitlesMap = async () => {
    try {
      const resp = await getObjectiveTypeTitles();
      const items = resp?.data || [];
      const map = items.reduce((acc: Record<string, string>, item: any) => {
        const objectiveType = item?.objectiveType;
        const titleTemplate =
          item?.titleTemplateTrans?.en || item?.titleTemplate || "";
        if (objectiveType && titleTemplate) {
          acc[objectiveType] = titleTemplate;
        }
        return acc;
      }, {});
      setObjectiveTypeTitleMap(map);
    } catch {
      // Keep local fallback examples if objective type titles cannot be loaded.
      setObjectiveTypeTitleMap({});
    }
  };

  const getOptionLabel = (
    options: { id: string; label: string }[],
    id?: string,
    fallback?: string,
  ) => {
    if (!id) return fallback || "";
    const found = options.find((opt) => opt.id === id);
    return found?.label || fallback || id;
  };

  const getObjectiveDynamicValues = (
    objectiveType: string,
    objectiveTarget: number | null,
    filters: ObjectiveFilters | null | undefined,
  ): string[] => {
    const safeFilters = filters || {};
    const targetValue = String(objectiveTarget ?? "N");
    const minPlayersValue = String(safeFilters.minPlayers ?? 3);
    const minOddsValue = String(safeFilters.minOdds ?? 2.0);
    const positionValue = getOptionLabel(
      positionGroupOptions,
      safeFilters.position,
      "Defender",
    );
    const nationalityValue = getOptionLabel(
      sessionHelperInstance.countryNameOptions || [],
      safeFilters.nationality,
      "English",
    );
    const clubValue = getOptionLabel(
      sessionHelperInstance.clubsOptions || [],
      safeFilters.clubId,
      "Arsenal",
    );
    const betTypeValue = getOptionLabel(
      bettingMarketOptions,
      safeFilters.betType,
      "Correct Score",
    );

    const map: Record<string, string[]> = {
      GOALS_SCORED: [targetValue],
      GOALS_BY_POSITION: [targetValue, positionValue],
      GOALS_BY_NATIONALITY: [targetValue, nationalityValue],
      GOALS_BY_CLUB: [targetValue, clubValue],
      CONSECUTIVE_WINS: [targetValue],
      CLEAN_SHEETS: [targetValue],
      CONSECUTIVE_CLEAN_SHEETS: [targetValue],
      CONSECUTIVE_SCORING: [targetValue],
      WINS_WITH_NATIONALITY: [targetValue, minPlayersValue, nationalityValue],
      WINS_WITH_CLUB: [targetValue, minPlayersValue, clubValue],
      BETS_PLACED: [targetValue],
      BETS_PLACED_MARKET: [targetValue, betTypeValue],
      BET_WIN_BY_ODDS: [targetValue, minOddsValue],
      LEAGUE_POINTS: [targetValue],
      VAULT_LEVEL_UP: [targetValue],
    };

    return map[objectiveType] || [targetValue];
  };

  const getFallbackDynamicExample = (
    objectiveType: string,
    objectiveTarget: number | null,
    filters: ObjectiveFilters | null | undefined,
  ) => {
    const baseExample = objectiveTypeDescriptions[objectiveType]?.example;
    if (!baseExample) return "";

    let resolved = baseExample.replace("N", String(objectiveTarget ?? "N"));
    const values = getObjectiveDynamicValues(
      objectiveType,
      objectiveTarget,
      filters,
    );

    if (objectiveType === "GOALS_BY_POSITION") {
      resolved = resolved.replace("Defender", values[1] || "Defender");
    }

    if (objectiveType === "GOALS_BY_NATIONALITY") {
      resolved = resolved.replace(
        "English players",
        `${values[1] || "English"} players`,
      );
    }

    if (objectiveType === "GOALS_BY_CLUB") {
      resolved = resolved.replace(
        "Arsenal players",
        `${values[1] || "Arsenal"} players`,
      );
    }

    if (objectiveType === "WINS_WITH_NATIONALITY") {
      resolved = resolved.replace(
        "3 Brazilian players",
        `${values[1] || "3"} ${values[2] || "Brazilian"} players`,
      );
    }

    if (objectiveType === "WINS_WITH_CLUB") {
      resolved = resolved.replace(
        "3 Arsenal players",
        `${values[1] || "3"} ${values[2] || "Arsenal"} players`,
      );
    }

    if (objectiveType === "BETS_PLACED_MARKET") {
      resolved = resolved.replace(
        "Correct Score markets",
        `${values[1] || "Correct Score"} markets`,
      );
    }

    if (objectiveType === "BET_WIN_BY_ODDS") {
      resolved = resolved.replace("2.0", values[1] || "2.0");
    }

    return resolved;
  };

  useEffect(() => {
    fetchObjectiveTypeConfigs();
    fetchObjectiveTypeTitlesMap();
    fetchStandardOddsKeys();
    if (isEdit) fetchChallengeInfo();
    // Fetch packs for reward selection
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      const resp = await getPlayerPacks({
        pageNo: defaultPageNo,
        pageSize: defaultPageSize,
      });
      if (resp?.items) {
        setPackOptions(
          resp.items.map((p: any) => ({ id: p._id, label: p.name })),
        );
      }
    } catch (err: any) {
      console.error("Failed to load packs:", err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePackSearch = useCallback(
    debounce(async (searchValue: string) => {
      try {
        const resp = await getPlayerPacks(
          searchValue
            ? { search: searchValue, skipPagination: true }
            : { pageNo: defaultPageNo, pageSize: defaultPageSize },
        );
        if (resp?.items) {
          const newOptions = resp.items.map((p: any) => ({
            id: p._id,
            label: p.name,
          }));
          setPackOptions((prev) =>
            searchValue
              ? getUniquesArrayObject([...newOptions, ...prev])
              : newOptions,
          );
        }
      } catch (err: any) {
        console.error("Pack search error:", err);
      }
    }, 300),
    [],
  );

  // ─── Helper: is pack-based reward ──────────────────────
  const isPackRewardType = (rt: string) =>
    ["PACK", "MYSTERY_PLAYER_CARD"].includes(rt);

  // ─── Conditional Filters Renderer ──────────────────────
  const renderObjectiveFilters = (
    objectiveType: string,
    filters: ObjectiveFilters,
    onFilterUpdate: (field: string, value: any) => void,
  ) => {
    return (
      <>
        {needsPositionFilter(objectiveType) && (
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Position Group"
              name="position"
              value={filters.position || ""}
              options={positionGroupOptions}
              onChange={(e: any) =>
                onFilterUpdate("position", e.target.value || undefined)
              }
            />
          </Box>
        )}

        {needsNationalityFilter(objectiveType) && (
          <>
            <Box className={styles.field}>
              <SearchableInput
                label="Nationality"
                name="nationality"
                selected={
                  filters.nationality
                    ? (() => {
                        const opt = (
                          sessionHelperInstance.countryNameOptions || []
                        ).find((o: any) => o.id === filters.nationality);
                        return (
                          opt || {
                            id: filters.nationality,
                            label: filters.nationality,
                          }
                        );
                      })()
                    : null
                }
                options={sessionHelperInstance.countryNameOptions || []}
                onOptionSelect={(e: any) => {
                  const val = e.target.value;
                  const id = Array.isArray(val)
                    ? val[0]?.id || val[0]
                    : val?.id || val;
                  onFilterUpdate("nationality", id || undefined);
                }}
                isMultiSelect={false}
                placeholder="Search nationality"
              />
            </Box>
            {needsMinPlayers(objectiveType) && (
              <Box className={styles.field}>
                <SimpleTextInput
                  label="Min Players from Nationality"
                  name="minPlayers"
                  type="number"
                  value={filters.minPlayers ?? ""}
                  onChange={(e: any) =>
                    onFilterUpdate(
                      "minPlayers",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  placeholder="1-11"
                />
              </Box>
            )}
          </>
        )}

        {needsClubFilter(objectiveType) && (
          <>
            <Box className={styles.field}>
              <SearchableInput
                label="Club"
                name="clubId"
                selected={
                  filters.clubId
                    ? (() => {
                        const opt = (
                          sessionHelperInstance.clubsOptions || []
                        ).find((o: any) => o.id === filters.clubId);
                        return (
                          opt || { id: filters.clubId, label: filters.clubId }
                        );
                      })()
                    : null
                }
                options={sessionHelperInstance.clubsOptions || []}
                onOptionSelect={(e: any) => {
                  const val = e.target.value;
                  const id = Array.isArray(val)
                    ? val[0]?.id || val[0]
                    : val?.id || val;
                  onFilterUpdate("clubId", id || undefined);
                }}
                isMultiSelect={false}
                placeholder="Search club"
              />
            </Box>
            {needsMinPlayers(objectiveType) && (
              <Box className={styles.field}>
                <SimpleTextInput
                  label="Min Players from Club"
                  name="minPlayers"
                  type="number"
                  value={filters.minPlayers ?? ""}
                  onChange={(e: any) =>
                    onFilterUpdate(
                      "minPlayers",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  placeholder="1-11"
                />
              </Box>
            )}
          </>
        )}

        {needsMinOdds(objectiveType) && (
          <>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Minimum Odds"
                name="minOdds"
                type="number"
                value={filters.minOdds ?? ""}
                onChange={(e: any) =>
                  onFilterUpdate(
                    "minOdds",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="e.g. 2.00"
              />
            </Box>
            {filters.minOdds != null &&
              filters.minOdds !== ("" as any) &&
              standardOddsKeys.size > 0 &&
              !standardOddsKeys.has(String(filters.minOdds)) && (
                <Box sx={{ width: "100%", mt: 1 }}>
                  <Alert severity="warning">
                    {filters.minOdds} is not a valid standard odds value. The
                    fractional display will be undefined in-game. Please use a
                    value from the standard odds table (e.g.{" "}
                    {getNearbyOddsExamples(filters.minOdds, standardOddsKeys)}
                    ).
                  </Alert>
                </Box>
              )}
          </>
        )}

        {needsBetTypeFilter(objectiveType) && (
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Betting Market Type"
              name="betType"
              value={filters.betType ?? ""}
              options={bettingMarketOptions}
              onChange={(e: any) =>
                onFilterUpdate("betType", e.target.value || undefined)
              }
            />
          </Box>
        )}
      </>
    );
  };

  // ─── Render ──────────────────────────────────────────────
  const isComboType = challengeDetails.type === "COMBO";
  const rewardSectionStep = isComboType ? 4 : 3;
  const scheduleSectionStep = isComboType ? 5 : 4;
  const settingsSectionStep = isComboType ? 6 : 5;

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${isEdit ? "Edit" : "Create"} Challenge`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button variant="outlined" color="inherit" onClick={cancel}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save Challenge
          </Button>
        </Box>
      }
    >
      <Box sx={{ maxWidth: 960, pb: 4 }}>
        {/* Warning for active live challenges */}
        {isEdit &&
          challengeDetails.isActive &&
          challengeDetails.startDate &&
          challengeDetails.endDate &&
          new Date(challengeDetails.startDate) <= new Date() &&
          new Date(challengeDetails.endDate) >= new Date() && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This challenge is currently active and live. You will not be able
              to save any changes. To edit this challenge, please deactivate it
              first.
            </Alert>
          )}
        {/* ── 1. Basic Information ─────────────────────────── */}
        <SectionCard
          step={1}
          icon={<PersonOutlineIcon fontSize="small" />}
          title="Basic Information"
          accent="#1565c0"
        >
          {/* Challenge Type — Visual Cards */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 1.5 }}
            >
              Challenge Type
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {/* Individual Card */}
              <Paper
                elevation={0}
                onClick={() =>
                  onChange({
                    target: { name: "type", value: "INDIVIDUAL" },
                  } as any)
                }
                sx={{
                  flex: "1 1 200px",
                  maxWidth: 340,
                  p: 2,
                  cursor: "pointer",
                  borderRadius: 2.5,
                  border: "2px solid",
                  borderColor:
                    challengeDetails.type === "INDIVIDUAL"
                      ? "#1565c0"
                      : "#e0e0e0",
                  bgcolor:
                    challengeDetails.type === "INDIVIDUAL"
                      ? "#e3f2fd"
                      : "white",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#1565c0",
                    bgcolor: "#e3f2fd80",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor:
                        challengeDetails.type === "INDIVIDUAL"
                          ? "#1565c0"
                          : "#e0e0e0",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    <FlagOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Individual
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Single objective challenge
                    </Typography>
                  </Box>
                  {challengeDetails.type === "INDIVIDUAL" && (
                    <CheckCircleIcon
                      sx={{ ml: "auto", color: "#1565c0", fontSize: 20 }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5 }}
                >
                  Player completes one specific objective to earn the reward.
                </Typography>
              </Paper>

              {/* Combo Card */}
              <Paper
                elevation={0}
                onClick={() =>
                  onChange({
                    target: { name: "type", value: "COMBO" },
                  } as any)
                }
                sx={{
                  flex: "1 1 200px",
                  maxWidth: 340,
                  p: 2,
                  cursor: "pointer",
                  borderRadius: 2.5,
                  border: "2px solid",
                  borderColor:
                    challengeDetails.type === "COMBO" ? "#6a1b9a" : "#e0e0e0",
                  bgcolor:
                    challengeDetails.type === "COMBO" ? "#f3e5f5" : "white",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#6a1b9a",
                    bgcolor: "#f3e5f580",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor:
                        challengeDetails.type === "COMBO"
                          ? "#6a1b9a"
                          : "#e0e0e0",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    <LayersIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Combo
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Multi-step challenge
                    </Typography>
                  </Box>
                  {challengeDetails.type === "COMBO" && (
                    <CheckCircleIcon
                      sx={{ ml: "auto", color: "#6a1b9a", fontSize: 20 }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5 }}
                >
                  Bundle multiple sub-challenges. Main reward granted only after
                  all sub-challenges are completed.
                </Typography>
              </Paper>
            </Box>
          </Box>

          {/* Tier — Color-coded Chips */}
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 1.5 }}
            >
              Tier
              {challengeDetails.type === "COMBO" ? " (optional for Combo)" : ""}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {challengeTierOptions.map((opt) => {
                const vis = tierVisuals[opt.id] || tierVisuals.OTHER;
                const isSelected = challengeDetails.tier === opt.id;
                return (
                  <Chip
                    key={opt.id}
                    label={`${vis.icon}  ${opt.label}`}
                    clickable
                    onClick={() =>
                      onChange({
                        target: {
                          name: "tier",
                          value: isSelected ? "" : opt.id,
                        },
                      } as any)
                    }
                    sx={{
                      px: 1,
                      height: 36,
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: isSelected ? vis.border : "#e0e0e0",
                      bgcolor: isSelected ? vis.bg : "white",
                      color: isSelected ? vis.text : "#616161",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: vis.border,
                        bgcolor: vis.bg,
                      },
                    }}
                  />
                );
              })}
            </Box>
            {challengeDetails.type === "COMBO" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Each sub-challenge has its own Tier. Root tier is optional for
                Combo challenges.
              </Typography>
            )}
          </Box>
        </SectionCard>

        {/* ── 2a. Objective Configuration (INDIVIDUAL) ─────── */}
        {challengeDetails.type === "INDIVIDUAL" && (
          <SectionCard
            step={2}
            icon={<FlagOutlinedIcon fontSize="small" />}
            title="Objective Configuration"
            subtitle="Define what the player needs to accomplish"
            accent="#6a1b9a"
            isComplete={
              !!challengeDetails.objectiveType &&
              (challengeDetails.objectiveTarget ?? 0) > 0
            }
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
              <Box className={styles.field}>
                <SimpleSelectInput
                  label="Objective Type"
                  name="objectiveType"
                  value={challengeDetails.objectiveType}
                  options={objectiveTypeOptions}
                  onChange={onChange}
                />
              </Box>
              <Box className={styles.field}>
                <SimpleTextInput
                  label="Objective Target"
                  name="objectiveTarget"
                  type="number"
                  value={challengeDetails.objectiveTarget ?? ""}
                  onChange={onChange}
                  placeholder="e.g. 5"
                />
              </Box>
            </Box>

            {/* Dynamic Example Card — Prominent */}
            {challengeDetails.objectiveType &&
              objectiveTypeDescriptions[challengeDetails.objectiveType] && (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    p: 2,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: `${
                      objectiveCategoryColor[challengeDetails.objectiveType] ||
                      "#1976d2"
                    }30`,
                    bgcolor: `${
                      objectiveCategoryColor[challengeDetails.objectiveType] ||
                      "#1976d2"
                    }08`,
                    borderLeft: `4px solid ${
                      objectiveCategoryColor[challengeDetails.objectiveType] ||
                      "#1976d2"
                    }`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: `${
                          objectiveCategoryColor[
                            challengeDetails.objectiveType
                          ] || "#1976d2"
                        }15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <LightbulbOutlinedIcon
                        sx={{
                          fontSize: 22,
                          color:
                            objectiveCategoryColor[
                              challengeDetails.objectiveType
                            ] || "#1976d2",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontSize: 10,
                        }}
                      >
                        Dynamic Example
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{
                          mt: 0.25,
                          color:
                            objectiveCategoryColor[
                              challengeDetails.objectiveType
                            ] || "#1976d2",
                        }}
                      >
                        {(() => {
                          const configuredTemplate =
                            objectiveTypeTitleMap[
                              challengeDetails.objectiveType
                            ] || "";
                          const values = getObjectiveDynamicValues(
                            challengeDetails.objectiveType,
                            challengeDetails.objectiveTarget,
                            challengeDetails.objectiveFilters,
                          );

                          if (configuredTemplate) {
                            return replaceTemplatePlaceholders(
                              configuredTemplate,
                              values,
                            );
                          }

                          return getFallbackDynamicExample(
                            challengeDetails.objectiveType,
                            challengeDetails.objectiveTarget,
                            challengeDetails.objectiveFilters,
                          );
                        })()}
                      </Typography>
                      {!!objectiveTypeTitleMap[
                        challengeDetails.objectiveType
                      ] && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          Template:{" "}
                          {
                            objectiveTypeTitleMap[
                              challengeDetails.objectiveType
                            ]
                          }
                        </Typography>
                      )}

                      {!objectiveTypeTitleMap[
                        challengeDetails.objectiveType
                      ] && (
                        <Alert
                          severity="warning"
                          variant="outlined"
                          sx={{ mt: 1, py: 0.25, borderRadius: 1.5 }}
                        >
                          No Objective Type Title configured for this objective
                          type yet. Please create one in Objective Type Titles.
                        </Alert>
                      )}

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {
                          objectiveTypeDescriptions[
                            challengeDetails.objectiveType
                          ].hint
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

            {/* Conditional filters */}
            {challengeDetails.objectiveType && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {renderObjectiveFilters(
                  challengeDetails.objectiveType,
                  challengeDetails.objectiveFilters,
                  onFilterChange,
                )}
              </Box>
            )}
          </SectionCard>
        )}

        {/* ── 2a-combo. Combo Title (COMBO only) ──────────── */}
        {challengeDetails.type === "COMBO" && (
          <SectionCard
            step={2}
            icon={<FlagOutlinedIcon fontSize="small" />}
            title="Combo Title"
            subtitle="Set the main title for this combo challenge"
            accent="#6a1b9a"
            isComplete={!!challengeDetails.comboTitle}
          >
            <Box className={styles.field}>
              <SimpleTextInput
                label="Combo Title"
                name="comboTitle"
                value={challengeDetails.comboTitle}
                onChange={(e: any) => {
                  setChallengeDetails((prev) => ({
                    ...prev,
                    comboTitle: e.target.value,
                    comboTitleTrans: {
                      ...prev.comboTitleTrans,
                      en: e.target.value,
                    },
                  }));
                }}
                button={
                  <TranslationButtons
                    field={"comboTitleTrans"}
                    onClick={(type: string, field: string) =>
                      handleOpenModal(type, field)
                    }
                  />
                }
                placeholder="e.g. Weekend Warrior Challenge"
              />
            </Box>
          </SectionCard>
        )}

        {/* ── 2b. Sub-Challenges (COMBO) ───────────────────── */}
        {challengeDetails.type === "COMBO" && (
          <SectionCard
            step={3}
            icon={<LayersIcon fontSize="small" />}
            title={`Sub-Challenges (${challengeDetails.subChallenges.length})`}
            subtitle="Each sub-challenge has its own objective and reward"
            accent="#6a1b9a"
            isComplete={
              challengeDetails.subChallenges.length > 0 &&
              challengeDetails.subChallenges.every(
                (s) => !!s.objectiveType && (s.objectiveTarget ?? 0) > 0,
              )
            }
          >
            {challengeDetails.subChallenges.length === 0 && (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ mb: 2, borderRadius: 2 }}
                icon={<InfoOutlinedIcon fontSize="small" />}
              >
                <Typography variant="body2">
                  Add at least one sub-challenge. Players must complete{" "}
                  <strong>every</strong> sub-challenge to earn the combo reward.
                </Typography>
              </Alert>
            )}

            {challengeDetails.subChallenges.map((sub, idx) => (
              <Accordion
                key={idx}
                defaultExpanded
                sx={{
                  mb: 2,
                  border: "1px solid",
                  borderColor:
                    sub.objectiveType && (sub.objectiveTarget ?? 0) > 0
                      ? "#a5d6a740"
                      : "divider",
                  borderRadius: "12px !important",
                  "&:before": { display: "none" },
                  boxShadow: "none",
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background:
                      "linear-gradient(135deg, #f5f0ff 0%, #ede7f6 100%)",
                    borderRadius: "12px 12px 0 0",
                    minHeight: 52,
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      gap: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #6a1b9a, #9c27b0)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      boxShadow: "0 2px 6px #6a1b9a40",
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ flex: 1 }}
                  >
                    {`Sub-Challenge #${idx + 1}`}
                  </Typography>
                  {sub.objectiveType && (
                    <Chip
                      label={
                        objectiveTypeOptions.find(
                          (o) => o.id === sub.objectiveType,
                        )?.label
                      }
                      size="small"
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        height: 22,
                        bgcolor: `${
                          objectiveCategoryColor[sub.objectiveType] || "#6a1b9a"
                        }15`,
                        color:
                          objectiveCategoryColor[sub.objectiveType] ||
                          "#6a1b9a",
                        border: `1px solid ${
                          objectiveCategoryColor[sub.objectiveType] || "#6a1b9a"
                        }30`,
                      }}
                    />
                  )}
                  {sub.rewardType && rewardTypeInfo[sub.rewardType] && (
                    <Chip
                      label={`${rewardTypeInfo[sub.rewardType].icon} ${
                        rewardTypeOptions.find((r) => r.id === sub.rewardType)
                          ?.label || ""
                      }`}
                      size="small"
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        height: 22,
                        bgcolor: "#fff3e0",
                        color: "#e65100",
                        border: "1px solid #ffe0b2",
                      }}
                    />
                  )}
                  {sub.objectiveType &&
                    (sub.objectiveTarget ?? 0) > 0 &&
                    sub.rewardType && (
                      <CheckCircleIcon
                        sx={{ fontSize: 18, color: "#4caf50", mr: 0.5 }}
                      />
                    )}
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSubChallenge(idx);
                    }}
                    size="small"
                    sx={{ mr: 0.5 }}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 2 }}>
                  {/* Sub objective fields */}
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}
                  >
                    <Box className={styles.field}>
                      <SimpleSelectInput
                        label="Objective Type"
                        name={`sub_objectiveType_${idx}`}
                        value={sub.objectiveType}
                        options={objectiveTypeOptions}
                        onChange={(e: any) =>
                          onSubChallengeChange(
                            idx,
                            "objectiveType",
                            e.target.value,
                          )
                        }
                      />
                    </Box>
                    <Box className={styles.field}>
                      <SimpleTextInput
                        label="Objective Target"
                        name={`sub_objectiveTarget_${idx}`}
                        type="number"
                        value={sub.objectiveTarget ?? ""}
                        onChange={(e: any) =>
                          onSubChallengeChange(
                            idx,
                            "objectiveTarget",
                            e.target.value,
                          )
                        }
                        placeholder="e.g. 3"
                      />
                    </Box>
                  </Box>

                  {/* Sub objective hint — enhanced */}
                  {sub.objectiveType &&
                    objectiveTypeDescriptions[sub.objectiveType] && (
                      <Paper
                        elevation={0}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: `${
                            objectiveCategoryColor[sub.objectiveType] ||
                            "#6a1b9a"
                          }06`,
                          borderLeft: `3px solid ${
                            objectiveCategoryColor[sub.objectiveType] ||
                            "#6a1b9a"
                          }`,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <LightbulbOutlinedIcon
                          sx={{
                            fontSize: 18,
                            color:
                              objectiveCategoryColor[sub.objectiveType] ||
                              "#6a1b9a",
                          }}
                        />
                        <Box>
                          {!!objectiveTypeTitleMap[sub.objectiveType] && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mb: 0.25 }}
                            >
                              Template:{" "}
                              {objectiveTypeTitleMap[sub.objectiveType]}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              color:
                                objectiveCategoryColor[sub.objectiveType] ||
                                "#6a1b9a",
                            }}
                          >
                            {(() => {
                              const configuredTemplate =
                                objectiveTypeTitleMap[sub.objectiveType] || "";
                              const values = getObjectiveDynamicValues(
                                sub.objectiveType,
                                sub.objectiveTarget,
                                sub.objectiveFilters,
                              );

                              if (configuredTemplate) {
                                return replaceTemplatePlaceholders(
                                  configuredTemplate,
                                  values,
                                );
                              }

                              return getFallbackDynamicExample(
                                sub.objectiveType,
                                sub.objectiveTarget,
                                sub.objectiveFilters,
                              );
                            })()}
                          </Typography>
                          {!objectiveTypeTitleMap[sub.objectiveType] && (
                            <Alert
                              severity="warning"
                              variant="outlined"
                              sx={{ mt: 0.75, py: 0.25, borderRadius: 1.5 }}
                            >
                              No Objective Type Title configured for this
                              objective type yet. Please create one in Objective
                              Type Titles.
                            </Alert>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {objectiveTypeDescriptions[sub.objectiveType].hint}
                          </Typography>
                        </Box>
                      </Paper>
                    )}

                  {/* Sub filters */}
                  {sub.objectiveType && (
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}
                    >
                      {renderObjectiveFilters(
                        sub.objectiveType,
                        sub.objectiveFilters,
                        (field, value) => onSubFilterChange(idx, field, value),
                      )}
                    </Box>
                  )}

                  {/* Sub-challenge Tier — Color Chips */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      Sub-Challenge Tier
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {challengeTierOptions.map((opt) => {
                        const vis = tierVisuals[opt.id] || tierVisuals.OTHER;
                        const isSelected = sub.tier === opt.id;
                        return (
                          <Chip
                            key={opt.id}
                            label={`${vis.icon}  ${opt.label}`}
                            clickable
                            size="small"
                            onClick={() =>
                              onSubChallengeChange(
                                idx,
                                "tier",
                                isSelected ? "" : opt.id,
                              )
                            }
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: 1.5,
                              border: "2px solid",
                              borderColor: isSelected ? vis.border : "#e0e0e0",
                              bgcolor: isSelected ? vis.bg : "white",
                              color: isSelected ? vis.text : "#616161",
                              transition: "all 0.2s",
                              "&:hover": {
                                borderColor: vis.border,
                                bgcolor: vis.bg,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Each sub-challenge can have its own tier. If left empty,
                      the root tier is used.
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Sub reward */}
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <EmojiEventsIcon
                      fontSize="small"
                      sx={{ color: "#f57c00" }}
                    />
                    Sub-Challenge Reward
                    {sub.rewardType && rewardTypeInfo[sub.rewardType] && (
                      <Chip
                        label={rewardTypeInfo[sub.rewardType].icon}
                        size="small"
                        sx={{ fontSize: 14, height: 22, ml: 0.5 }}
                      />
                    )}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box className={styles.field}>
                      <SimpleSelectInput
                        label="Reward Type"
                        name={`sub_rewardType_${idx}`}
                        value={sub.rewardType}
                        options={rewardTypeOptions}
                        onChange={(e: any) =>
                          onSubChallengeChange(
                            idx,
                            "rewardType",
                            e.target.value,
                          )
                        }
                      />
                    </Box>
                    <Box sx={{ width: 140 }}>
                      <SimpleTextInput
                        label={
                          isPackRewardType(sub.rewardType)
                            ? "Quantity (packs)"
                            : "Reward Value"
                        }
                        name={`sub_rewardValue_${idx}`}
                        type="number"
                        value={sub.rewardValue ?? ""}
                        onChange={(e: any) => {
                          onSubChallengeChange(
                            idx,
                            "rewardValue",
                            e.target.value,
                          );
                        }}
                        placeholder={
                          isPackRewardType(sub.rewardType)
                            ? "e.g. 2"
                            : "e.g. 50"
                        }
                      />
                    </Box>
                  </Box>

                  {/* Sub-challenge reward image — shown for ALL reward types */}
                  {sub.rewardType && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Reward Image
                      </Typography>
                      <FilePicker
                        value={subRewardImages[idx] ?? null}
                        name={`sub_${idx}_rewardImage`}
                        onFileSelect={(img: any, dims?: any) => {
                          setSubRewardImages((prev) => {
                            const next = [...prev];
                            next[idx] = img;
                            return next;
                          });
                          setSubRewardImageDimensions((prev) => {
                            const next = [...prev];
                            next[idx] = dims ?? null;
                            return next;
                          });
                        }}
                        imageType={ImageType.CHALLENGE_REWARD_IMAGE}
                        enableDimensionControl={true}
                        metaData={{
                          note: `Recommended: ${DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE].width}×${DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE].height}px. Resized to WebP.`,
                        }}
                      />
                      {sub.rewardImageUrl && !subRewardImages[idx] && (
                        <Box
                          component="img"
                          src={sub.rewardImageUrl}
                          alt={`Sub ${idx + 1} Reward`}
                          sx={{
                            width: 56,
                            mt: 0.5,
                            borderRadius: 1,
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Sub-challenge pack selectors — multi-select SearchableInput */}
                  {isPackRewardType(sub.rewardType) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Select Reward Packs
                      </Typography>
                      <Box className={styles.field}>
                        <SearchableInput
                          label="Reward Packs"
                          name={`sub_${idx}_rewardPacks`}
                          isMultiSelect={true}
                          selected={subPackSelections[idx] ?? []}
                          options={packOptions}
                          maxSelections={
                            Number.isInteger(Number(sub.rewardValue)) &&
                            Number(sub.rewardValue) > 0
                              ? Number(sub.rewardValue)
                              : undefined
                          }
                          onMaxSelectionReached={() => {
                            const allowedCount = Number(sub.rewardValue);
                            showSnackbar(
                              `You can select only ${allowedCount} pack ID${allowedCount > 1 ? "s" : ""} for Sub-challenge #${idx + 1}`,
                              "error",
                            );
                          }}
                          onInputChange={handlePackSearch}
                          onOptionSelect={(e: any) => {
                            const selections = e.target.value || [];
                            const packIds = selections
                              .map((s: any) => s?.id || "")
                              .filter(Boolean);
                            onSubChallengeChange(idx, "rewardPacks", packIds);
                            setSubPackSelections((prev) => {
                              const next = prev.map((row) => [...row]);
                              while (next.length <= idx) next.push([]);
                              next[idx] = selections;
                              return next;
                            });
                          }}
                          placeholder="Search and select packs"
                        />
                      </Box>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}

            <Box sx={{ textAlign: "center", pt: 1.5 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addSubChallenge}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  borderStyle: "dashed",
                  borderWidth: 2,
                  borderColor: "#6a1b9a40",
                  color: "#6a1b9a",
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#6a1b9a",
                    bgcolor: "#f3e5f5",
                    borderStyle: "dashed",
                  },
                }}
              >
                Add Sub-Challenge
              </Button>
            </Box>
          </SectionCard>
        )}

        {/* ── 3. Reward Configuration ─────────────────────── */}
        <SectionCard
          step={rewardSectionStep}
          icon={<EmojiEventsIcon fontSize="small" />}
          title={
            challengeDetails.type === "COMBO"
              ? "Combo Completion Reward"
              : "Reward Configuration"
          }
          accent="#e65100"
        >
          {challengeDetails.type === "COMBO" && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ mb: 2, py: 0.5, borderRadius: 1 }}
            >
              This reward is given{" "}
              <strong>only after all sub-challenges are completed</strong>. Each
              sub-challenge above can also carry its own separate reward.
            </Alert>
          )}

          {/* Combo Completion Reward Title */}
          {challengeDetails.type === "COMBO" && (
            <Box sx={{ mb: 2 }}>
              <Box className={styles.field}>
                <SimpleTextInput
                  label="Reward Title"
                  name="comboRewardTitle"
                  value={challengeDetails.comboRewardTitle}
                  onChange={(e: any) => {
                    setChallengeDetails((prev) => ({
                      ...prev,
                      comboRewardTitle: e.target.value,
                      comboRewardTitleTrans: {
                        ...prev.comboRewardTitleTrans,
                        en: e.target.value,
                      },
                    }));
                  }}
                  button={
                    <TranslationButtons
                      field={"comboRewardTitleTrans"}
                      onClick={(type: string, field: string) =>
                        handleOpenModal(type, field)
                      }
                    />
                  }
                  placeholder="e.g. Ultimate Combo Reward"
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <Box className={styles.field}>
              <SimpleSelectInput
                label="Reward Type"
                name="rewardType"
                value={challengeDetails.rewardType}
                options={rewardTypeOptions}
                onChange={onChange}
              />
            </Box>
            <Box sx={{ width: 140 }}>
              <SimpleTextInput
                label={
                  isPackRewardType(challengeDetails.rewardType)
                    ? "Quantity (number of packs)"
                    : "Reward Value"
                }
                name="rewardValue"
                type="number"
                value={challengeDetails.rewardValue ?? ""}
                onChange={onChange}
                placeholder={
                  isPackRewardType(challengeDetails.rewardType)
                    ? "e.g. 2"
                    : "e.g. 100"
                }
              />
            </Box>
          </Box>

          {/* Reward type info card */}
          {challengeDetails.rewardType &&
            rewardTypeInfo[challengeDetails.rewardType] && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: `${
                    rewardTypeInfo[challengeDetails.rewardType].color
                  }08`,
                  border: `1px solid ${
                    rewardTypeInfo[challengeDetails.rewardType].color
                  }20`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 28, lineHeight: 1 }}>
                  {rewardTypeInfo[challengeDetails.rewardType].icon}
                </Typography>
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {
                      rewardTypeOptions.find(
                        (r) => r.id === challengeDetails.rewardType,
                      )?.label
                    }
                    {challengeDetails.rewardValue
                      ? ` — ${challengeDetails.rewardValue}`
                      : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {rewardTypeInfo[challengeDetails.rewardType].desc}
                  </Typography>
                </Box>
              </Paper>
            )}

          {/* Reward Image — shown for ALL reward types (shared image) */}
          {challengeDetails.rewardType && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Reward Image
              </Typography>
              <FilePicker
                value={rewardImage ?? null}
                name="rewardImage"
                onFileSelect={(img: any, dims?: any) => {
                  setRewardImage(img);
                  setRewardImageDimensions(dims ?? null);
                }}
                imageType={ImageType.CHALLENGE_REWARD_IMAGE}
                enableDimensionControl={true}
                metaData={{
                  note: `Recommended: ${DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE].width}×${DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE].height}px. Resized to WebP.`,
                }}
              />
              {challengeDetails.rewardImageUrl && !rewardImage && (
                <Box
                  component="img"
                  src={challengeDetails.rewardImageUrl}
                  alt="Reward"
                  sx={{ width: 56, mt: 0.5, borderRadius: 1 }}
                />
              )}
            </Box>
          )}

          {/* Pack selectors — multi-select SearchableInput */}
          {isPackRewardType(challengeDetails.rewardType) && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Select Reward Packs
              </Typography>
              <Box className={styles.field}>
                <SearchableInput
                  label="Reward Packs"
                  name="rewardPacks"
                  isMultiSelect={true}
                  selected={packSelections}
                  options={packOptions}
                  maxSelections={
                    Number.isInteger(Number(challengeDetails.rewardValue)) &&
                    Number(challengeDetails.rewardValue) > 0
                      ? Number(challengeDetails.rewardValue)
                      : undefined
                  }
                  onMaxSelectionReached={() => {
                    const allowedCount = Number(challengeDetails.rewardValue);
                    showSnackbar(
                      `You can select only ${allowedCount} pack ID${allowedCount > 1 ? "s" : ""} for the configured quantity`,
                      "error",
                    );
                  }}
                  onInputChange={handlePackSearch}
                  onOptionSelect={(e: any) => {
                    const selections = e.target.value || [];
                    const packIds = selections
                      .map((s: any) => s?.id || "")
                      .filter(Boolean);
                    setChallengeDetails((prev) => ({
                      ...prev,
                      rewardPacks: packIds,
                    }));
                    setPackSelections(selections);
                  }}
                  placeholder="Search and select packs"
                />
              </Box>
            </Box>
          )}
        </SectionCard>

        {/* ── 4. Schedule ─────────────────────────────────── */}
        <SectionCard
          step={scheduleSectionStep}
          icon={<CalendarMonthIcon fontSize="small" />}
          title="Schedule"
          subtitle="When and how often the challenge runs"
          accent="#2e7d32"
          isComplete={
            !!challengeDetails.startDate && !!challengeDetails.endDate
          }
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Box className={styles.field}>
              <SimpleTimeDateInput
                label="Start Date"
                name="startDate"
                value={challengeDetails.startDate || null}
                format="dd/MM/yyyy HH:mm:ss"
                minutesStep={1}
                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                views={["year", "month", "day", "hours", "minutes", "seconds"]}
                readOnly={false}
                removeBottomMargin={true}
                onChange={(value: any) => onDateChange("startDate", value)}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTimeDateInput
                label="End Date"
                name="endDate"
                value={challengeDetails.endDate || null}
                format="dd/MM/yyyy HH:mm:ss"
                minutesStep={1}
                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                views={["year", "month", "day", "hours", "minutes", "seconds"]}
                readOnly={false}
                removeBottomMargin={true}
                onChange={(value: any) => onDateChange("endDate", value)}
              />
            </Box>
          </Box>

          {/* Schedule Type — Visual Cards */}
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 1.5 }}
          >
            Schedule Type
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
            {scheduleTypeOptions.map((opt) => {
              const info = scheduleInfo[opt.id];
              const isSelected = challengeDetails.scheduleType === opt.id;
              return (
                <Paper
                  key={opt.id}
                  elevation={0}
                  onClick={() =>
                    onChange({
                      target: {
                        name: "scheduleType",
                        value: opt.id,
                      },
                    } as any)
                  }
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: isSelected ? "#2e7d32" : "#e0e0e0",
                    bgcolor: isSelected ? "#e8f5e9" : "white",
                    transition: "all 0.2s",
                    minWidth: 100,
                    textAlign: "center",
                    "&:hover": {
                      borderColor: "#2e7d32",
                      bgcolor: "#e8f5e980",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 20, mb: 0.25 }}>
                    {info?.icon || "📋"}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={isSelected ? 700 : 500}
                  >
                    {opt.label}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {/* Schedule description */}
          {challengeDetails.scheduleType &&
            scheduleInfo[challengeDetails.scheduleType] && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                {scheduleInfo[challengeDetails.scheduleType].desc}
              </Typography>
            )}

          {/* repeatEveryDays — required when CUSTOM is selected */}
          {challengeDetails.scheduleType === "CUSTOM" && (
            <Box
              sx={{
                mt: 2,
                width: "fit-content",
                minWidth: 220,
                maxWidth: "100%",
                p: 2,
                borderRadius: 2,
                border: "2px solid #2e7d32",
                bgcolor: "#e8f5e9",
                boxSizing: "border-box",
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                ⚙️ Cycle Interval (days){" "}
                <Typography component="span" variant="caption" color="error">
                  *
                </Typography>
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                How many days each cycle lasts. e.g. 3 means the challenge
                resets every 3 days.
              </Typography>
              <input
                type="number"
                min={1}
                placeholder="e.g. 3"
                value={challengeDetails.repeatEveryDays ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setChallengeDetails((prev) => ({
                    ...prev,
                    repeatEveryDays: val ? Number(val) : null,
                  }));
                }}
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: `2px solid ${
                    !challengeDetails.repeatEveryDays ? "#e53935" : "#2e7d32"
                  }`,
                  fontSize: 14,
                  outline: "none",
                  background: "white",
                }}
              />
              {!challengeDetails.repeatEveryDays && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Please enter a cycle interval to continue.
                </Typography>
              )}
            </Box>
          )}
        </SectionCard>

        {/* ── 5. Targeting & Settings ───────────────────────── */}
        <SectionCard
          step={settingsSectionStep}
          icon={<TuneIcon fontSize="small" />}
          title="Targeting & Settings"
          subtitle="Control who sees this challenge and how it behaves"
          accent="#37474f"
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ minWidth: 280, flex: "1 1 280px" }}>
              <SearchableInput
                label="Operators (leave empty for all)"
                name="operators"
                isMultiSelect={true}
                selected={(challengeDetails.operators || []).map(
                  (opId: any) => {
                    const opt = (
                      sessionHelperInstance.operatorOptions || []
                    ).find((o: any) => o.id === opId);
                    return opt || { id: opId, label: opId };
                  },
                )}
                options={sessionHelperInstance.operatorOptions || []}
                onOptionSelect={(e: any) => {
                  const selections = e.target.value || [];
                  const newIds = selections
                    .map((s: any) => s?.id || s)
                    .filter(Boolean);
                  setChallengeDetails((prev) => ({
                    ...prev,
                    operators: newIds,
                    // Remove any newly-selected operators from the excluded list
                    excludedOperators: (prev.excludedOperators || []).filter(
                      (id: any) => !newIds.includes(id),
                    ),
                  }));
                }}
                placeholder="Search operators"
              />
            </Box>
            <Box sx={{ minWidth: 280, flex: "1 1 280px" }}>
              <SearchableInput
                label="Excluded Operators"
                name="excludedOperators"
                isMultiSelect={true}
                selected={(challengeDetails.excludedOperators || []).map(
                  (opId: any) => {
                    const opt = (
                      sessionHelperInstance.operatorOptions || []
                    ).find((o: any) => o.id === opId);
                    return opt || { id: opId, label: opId };
                  },
                )}
                options={sessionHelperInstance.operatorOptions || []}
                onOptionSelect={(e: any) => {
                  const selections = e.target.value || [];
                  const newIds = selections
                    .map((s: any) => s?.id || s)
                    .filter(Boolean);
                  setChallengeDetails((prev) => ({
                    ...prev,
                    excludedOperators: newIds,
                    // Remove any newly-excluded operators from the included list
                    operators: (prev.operators || []).filter(
                      (id: any) => !newIds.includes(id),
                    ),
                  }));
                }}
                placeholder="Search operators to exclude"
              />
            </Box>
            {/* <Box className={styles.field}>
              <SimpleTextInput
                label="Sort Order"
                name="sortOrder"
                type="number"
                value={challengeDetails.sortOrder ?? ""}
                onChange={onChange}
                placeholder="e.g. 1"
              />
            </Box> */}
            <Box sx={{ alignSelf: "center" }}>
              {!isEdit ? (
                <SimpleSwitch
                  legendText="Challenge Status"
                  name="isActive"
                  value={challengeDetails.isActive}
                  label={challengeDetails.isActive ? "Active" : "Inactive"}
                  onChange={onChange}
                />
              ) : (
                <>
                  <FormLabel style={{ fontSize: 14 }} component="legend">
                    Challenge Status
                  </FormLabel>
                  <Box
                    sx={{
                      fontSize: "14px",
                      color: challengeDetails.isActive ? "#2e7d32" : "#c62828",
                      fontWeight: 600,
                      backgroundColor: challengeDetails.isActive
                        ? "#e8f5e9"
                        : "#fce4ec",
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      border: `1px solid ${
                        challengeDetails.isActive ? "#a5d6a7" : "#ef9a9a"
                      }`,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {challengeDetails.isActive ? (
                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />
                    )}
                    {challengeDetails.isActive ? "Active" : "Inactive"}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Change via Enable/Disable in the challenges list
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </SectionCard>

        {/* ── Bulk Translation Upload (COMBO only) ── */}
        {isEdit && challengeDetails.type === "COMBO" && (
          <SectionCard
            step={settingsSectionStep + 1}
            icon={<TranslateIcon fontSize="small" />}
            title="Bulk Translation Upload"
            subtitle="Upload translations for combo titles in bulk via Excel"
            accent="#00838f"
          >
            <Box
              sx={{
                "& .MuiCard-root": {
                  boxShadow: "none",
                  border: "none",
                  p: 0,
                  borderRadius: 0,
                  "&:hover": { boxShadow: "none" },
                },
              }}
            >
              <BulkTranslationUpload
                assetType="CHALLENGE"
                assetId={id}
                fields={[
                  { label: "Combo Title", fieldName: "comboTitleTrans" },
                  {
                    label: "Combo Reward Title",
                    fieldName: "comboRewardTitleTrans",
                  },
                ]}
                onSuccess={fetchChallengeInfo}
              />
            </Box>
          </SectionCard>
        )}
        {!isEdit && challengeDetails.type === "COMBO" && (
          <SectionCard
            step={settingsSectionStep + 1}
            icon={<TranslateIcon fontSize="small" />}
            title="Bulk Translation Upload"
            subtitle="Upload translations for combo titles in bulk via Excel"
            accent="#00838f"
          >
            <Box
              sx={{
                "& .MuiCard-root": {
                  boxShadow: "none",
                  border: "none",
                  p: 0,
                  borderRadius: 0,
                  "&:hover": { boxShadow: "none" },
                },
              }}
            >
              <BulkTranslationUpload
                assetType="CHALLENGE"
                fields={[
                  { label: "Combo Title", fieldName: "comboTitleTrans" },
                  {
                    label: "Combo Reward Title",
                    fieldName: "comboRewardTitleTrans",
                  },
                ]}
                deferUpload
                onFileChange={setTranslationFile}
              />
            </Box>
          </SectionCard>
        )}
      </Box>

      {/* ── Translation Modal ── */}
      <ViewLanguageModal
        isMultiline={true}
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleModalChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignBody: "center",
            alignHead: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default ChallengesEditor;
